import React, { useState, useEffect } from 'react';
import axios from 'axios';
import usePagination from '../../../utils/usePagination';
import Pagination from '../../../components/common/Pagination';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const EMPTY = {
  worker_name: '',
  work_type: '',
  work_date: new Date().toISOString().split('T')[0],
  days_worked: '1',
  daily_wage: '',
  crop_id: '',
  land_id: '',
  allocation_type: 'crop',
  payment_status: 'paid',
  notes: ''
};

const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' };

const WORK_TYPES = ['Sowing', 'Irrigation', 'Fertilizing', 'Weeding', 'Pesticide Spray', 'Harvesting', 'Threshing', 'Loading/Transport', 'Land Preparation', 'Other'];

const LaborTab = () => {
  const [labor, setLabor] = useState([]);
  const [crops, setCrops] = useState([]);
  const [lands, setLands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchAll = () => {
    Promise.all([
      axios.get(`${API}/agriculture/labor`, { headers: headers() }),
      axios.get(`${API}/agriculture/crops`, { headers: headers() }),
      axios.get(`${API}/agriculture/lands`, { headers: headers() })
    ]).then(([l, c, ld]) => {
      setLabor(l.data.data); setCrops(c.data.data); setLands(ld.data.data); setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (l) => {
    setForm({
      ...l,
      allocation_type: l.allocation_type || 'crop',
      work_date: l.work_date?.split('T')[0] || ''
    });
    setEditId(l.id);
    setShowForm(true);
  };

  // When allocation type changes, clear the irrelevant linkage
  const handleAllocationChange = (newType) => {
    if (newType === 'crop') {
      setForm({ ...form, allocation_type: 'crop', land_id: '' });
    } else {
      setForm({ ...form, allocation_type: 'land', crop_id: '' });
    }
  };

  const handleSave = async () => {
    if (!form.worker_name || !form.work_type || !form.daily_wage) {
      return alert('Worker name, work type and daily wage are required');
    }
    if (form.allocation_type === 'crop' && !form.crop_id) {
      return alert('Please select a crop for crop-level allocation');
    }
    if (form.allocation_type === 'land' && !form.land_id) {
      return alert('Please select a field for land-level allocation');
    }
    setSaving(true);
    try {
      if (editId) await axios.put(`${API}/agriculture/labor/${editId}`, form, { headers: headers() });
      else await axios.post(`${API}/agriculture/labor`, form, { headers: headers() });
      setShowForm(false); fetchAll();
    } catch { alert('Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this labor entry?')) return;
    await axios.delete(`${API}/agriculture/labor/${id}`, { headers: headers() });
    fetchAll();
  };

  const fmt = (n) => `Rs ${Number(n || 0).toLocaleString()}`;
  const totalCost = labor.reduce((s, l) => s + parseFloat(l.total_amount || 0), 0);
  const pendingCost = labor.filter(l => l.payment_status === 'pending').reduce((s, l) => s + parseFloat(l.total_amount || 0), 0);
  const filtered = filter === 'all' ? labor : labor.filter(l => l.payment_status === filter);
  const { paginatedData, currentPage, setCurrentPage, totalPages } = usePagination(filtered, 10);

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>Labor Management</h2>
        <button onClick={openAdd} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>+ Add Labor</button>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Total Records', value: labor.length, color: '#3498db', icon: '👷' },
          { label: 'Total Cost', value: fmt(totalCost), color: '#e74c3c', icon: '💰' },
          { label: 'Pending Payment', value: fmt(pendingCost), color: '#f39c12', icon: '⏳' },
          { label: 'Paid', value: labor.filter(l => l.payment_status === 'paid').length, color: '#27ae60', icon: '✅' },
        ].map(c => (
          <div key={c.label} style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: `4px solid ${c.color}` }}>
            <div style={{ fontSize: '24px', marginBottom: '6px' }}>{c.icon}</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: c.color }}>{c.value}</div>
            <div style={{ fontSize: '13px', color: '#7f8c8d' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {['all', 'paid', 'pending'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '8px 20px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
            background: filter === f ? '#27ae60' : '#f0f0f0', color: filter === f ? 'white' : '#555'
          }}>{f.charAt(0).toUpperCase() + f.slice(1)} ({f === 'all' ? labor.length : labor.filter(l => l.payment_status === f).length})</button>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['Worker', 'Work Type', 'Date', 'Days', 'Daily Wage', 'Total', 'Allocation', 'Linked To', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>No labor records found.</td></tr>
            ) : paginatedData.map((l, i) => {
              const allocType = l.allocation_type || 'crop';
              const allocColor = allocType === 'land' ? '#9b59b6' : '#27ae60';
              const linkedTo = allocType === 'land'
                ? (l.field_name || '—')
                : (l.crop_name || '—');
              return (
                <tr key={l.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                  <td style={{ padding: '14px 16px', fontWeight: '600', color: '#2c3e50' }}>{l.worker_name}</td>
                  <td style={{ padding: '14px 16px', color: '#555' }}>{l.work_type}</td>
                  <td style={{ padding: '14px 16px', color: '#555' }}>{new Date(l.work_date).toLocaleDateString('en-PK')}</td>
                  <td style={{ padding: '14px 16px', color: '#555' }}>{l.days_worked}</td>
                  <td style={{ padding: '14px 16px', color: '#555' }}>{fmt(l.daily_wage)}</td>
                  <td style={{ padding: '14px 16px', fontWeight: '700', color: '#e74c3c' }}>{fmt(l.total_amount)}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: `${allocColor}20`, color: allocColor }}>
                      {allocType === 'land' ? '🌍 Land (split)' : '🌾 Crop'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', color: '#555' }}>{linkedTo}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: l.payment_status === 'paid' ? '#27ae6020' : '#f39c1220', color: l.payment_status === 'paid' ? '#27ae60' : '#f39c12' }}>
                      {l.payment_status}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => openEdit(l)} style={{ padding: '6px 14px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
                      <button onClick={() => handleDelete(l.id)} style={{ padding: '6px 14px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalRecords={filtered.length}
          rowsPerPage={10}
        />
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', marginBottom: '24px' }}>{editId ? 'Edit Labor Entry' : 'Add Labor Entry'}</h3>

            {/* Allocation Type Toggle */}
            <div style={{ marginBottom: '24px', padding: '16px', background: '#f8f9fa', borderRadius: '10px' }}>
              <label style={{ ...labelStyle, marginBottom: '12px' }}>Cost Allocation *</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => handleAllocationChange('crop')}
                  style={{
                    flex: 1,
                    padding: '14px',
                    border: form.allocation_type === 'crop' ? '2px solid #27ae60' : '2px solid #ddd',
                    background: form.allocation_type === 'crop' ? '#e8f5e9' : 'white',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ fontWeight: '700', color: form.allocation_type === 'crop' ? '#27ae60' : '#555', fontSize: '14px' }}>
                    🌾 For specific crop
                  </div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                    Full cost goes to one crop
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleAllocationChange('land')}
                  style={{
                    flex: 1,
                    padding: '14px',
                    border: form.allocation_type === 'land' ? '2px solid #9b59b6' : '2px solid #ddd',
                    background: form.allocation_type === 'land' ? '#f3e5f5' : 'white',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ fontWeight: '700', color: form.allocation_type === 'land' ? '#9b59b6' : '#555', fontSize: '14px' }}>
                    🌍 For whole land
                  </div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                    Cost auto-splits among crops by kanal
                  </div>
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Worker Name *</label>
                <input style={inputStyle} value={form.worker_name} onChange={e => setForm({ ...form, worker_name: e.target.value })} placeholder="e.g. Ahmad Ali" />
              </div>
              <div>
                <label style={labelStyle}>Work Type *</label>
                <select style={inputStyle} value={form.work_type} onChange={e => setForm({ ...form, work_type: e.target.value })}>
                  <option value="">-- Select --</option>
                  {WORK_TYPES.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Work Date</label>
                <input style={inputStyle} type="date" value={form.work_date} onChange={e => setForm({ ...form, work_date: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Days Worked</label>
                <input style={inputStyle} type="number" step="0.5" value={form.days_worked} onChange={e => setForm({ ...form, days_worked: e.target.value })} placeholder="1" />
              </div>
              <div>
                <label style={labelStyle}>Daily Wage (Rs) *</label>
                <input style={inputStyle} type="number" value={form.daily_wage} onChange={e => setForm({ ...form, daily_wage: e.target.value })} placeholder="0" />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Total Amount</label>
                <input style={{ ...inputStyle, background: '#f8f9fa', color: '#27ae60', fontWeight: '700' }} value={`Rs ${(parseFloat(form.days_worked || 0) * parseFloat(form.daily_wage || 0)).toLocaleString()}`} readOnly />
              </div>

              {/* Conditional crop OR land selector based on allocation_type */}
              {form.allocation_type === 'crop' ? (
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Linked Crop *</label>
                  <select style={inputStyle} value={form.crop_id} onChange={e => setForm({ ...form, crop_id: e.target.value })}>
                    <option value="">-- Select Crop --</option>
                    {crops.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.crop_name} {c.field_name ? `(${c.field_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Linked Field *</label>
                  <select style={inputStyle} value={form.land_id} onChange={e => setForm({ ...form, land_id: e.target.value })}>
                    <option value="">-- Select Field --</option>
                    {lands.map(l => (
                      <option key={l.id} value={l.id}>
                        {l.field_name} ({l.total_kanals || 0} kanals)
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '12px', color: '#9b59b6', marginTop: '6px', fontWeight: '600' }}>
                    💡 Cost will be auto-split among all active crops on this field, proportional to their kanal area.
                  </div>
                </div>
              )}

              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Payment Status</label>
                <select style={inputStyle} value={form.payment_status} onChange={e => setForm({ ...form, payment_status: e.target.value })}>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea style={{ ...inputStyle, height: '70px', resize: 'vertical' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any notes..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '12px 24px', background: '#f0f0f0', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                {saving ? 'Saving...' : editId ? 'Update' : 'Add Labor'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LaborTab;